import React, { useState, useEffect } from 'react';
import { Search, Filter, Edit2, Save, X, AlertTriangle, Shield, Activity, ChevronLeft, ChevronRight } from 'lucide-react'; // Import Pagination Icons

const FirewallDashboard = () => {
  const [rules, setRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCriteria, setFilterCriteria] = useState({
    severity: '',
    policy_mode: '',
    rule_type: ''
  });
  const [editingRule, setEditingRule] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Number of items to display per page
  const [isLoading, setIsLoading] = useState(true); // Add loading state
 

  const [totalPages, setTotalPages] = useState(1);
  const [totalRules, setTotalRules] = useState(0);
  const [mediumSeverityRules, setMediumSeverityRules] = useState(0);
  const [highSeverityRules, setHighSeverityRules] = useState(0);



  // Function to build the query string
  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (filterCriteria.severity) params.append('severity', filterCriteria.severity);
    if (filterCriteria.policy_mode) params.append('policy_mode', filterCriteria.policy_mode);
    if (filterCriteria.rule_type) params.append('rule_type', filterCriteria.rule_type);
    params.append('page', currentPage); //Always Include Page and limit
    params.append('limit', itemsPerPage);
    return params.toString();
  };


  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const queryString = buildQueryString();
        const apiUrl = `/api/firewall-rules?${queryString}`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && Array.isArray(data.rules)) {
          setRules(data.rules);
          setTotalPages(data.totalPages || 1);
          setTotalRules(data.totalRules || 0); // Set the values from the response
          setMediumSeverityRules(data.mediumSeverityRules || 0);
          setHighSeverityRules(data.highSeverityRules || 0);
        } else {
          console.warn("Unexpected data format from API:", data);
          setRules([]);
          setTotalPages(1);
          setTotalRules(0);
          setMediumSeverityRules(0);
          setHighSeverityRules(0);
        }

      } catch (error) {
        console.error("Error fetching rules:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchTerm, filterCriteria, currentPage, itemsPerPage]);
  // First, create the severity indicator component
const SeverityIndicator = ({ severity }) => {
    const getStyles = (severity) => {
      const baseClasses = "px-3 py-1.5 rounded-full font-medium inline-flex items-center gap-2 transition-all duration-300";
      switch (severity) {
        case 'high':
          return `${baseClasses} bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-200 border border-red-500/50 hover:from-red-500/30 hover:to-red-600/30`;
        case 'medium':
          return `${baseClasses} bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-200 border border-yellow-500/50 hover:from-yellow-500/30 hover:to-amber-500/30`;
        case 'low':
          return `${baseClasses} bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-200 border border-green-500/50 hover:from-green-500/30 hover:to-emerald-500/30`;
        default:
          return `${baseClasses} bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-200 border border-gray-500/50 hover:from-gray-500/30 hover:to-gray-600/30`;
      }
    };
  
    const getIcon = (severity) => {
      switch (severity) {
        case 'high':
          return "●";
        case 'medium':
          return "●";
        case 'low':
          return "●";
        default:
          return "●";
      }
    };
  
    return (
      <span className={getStyles(severity)}>
        <span className="text-xs">{getIcon(severity)}</span>
        <span className="text-xs font-semibold">{severity}</span>
      </span>
    );
  };
  

  // Handlers (Search, Filters, Pagination)
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilterCriteria(prevCriteria => ({
      ...prevCriteria,
      [filterName]: value,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  
const handleDelete = async (ruleId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this rule?");
    if (!confirmDelete) return;

    try {
        const response = await fetch('/Delete/firewall-rules', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: ruleId })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        alert(data.message || "Rule deleted successfully!");
        window.location.reload(); // Reload the page after successful deletion

    } catch (error) {
        console.error("Error deleting rule:", error);
        alert("Failed to delete rule. Please try again later.");
    }
};

  // Filter rules based on search and criteria (client-side filtering is now redundant)
  useEffect(() => {
    // Since we're sending filtering to the server, client-side filtering is no longer necessary.
    // The filteredRules state is now updated based on the server's response.
    setFilteredRules(rules);  // Keep filteredRules in sync with rules from the server
  }, [rules]);



  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = rules.slice(indexOfFirstItem, indexOfLastItem);

  /*console.log("currentPage:", currentPage);
  console.log("itemsPerPage:", itemsPerPage);
  console.log("indexOfFirstItem:", indexOfFirstItem);
  console.log("indexOfLastItem:", indexOfLastItem);
  console.log("rules.length:", rules.length);
  console.log("currentItems:", currentItems);*/

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Assuming you have an API call like this:
const handleEdit = async (rule) => {
    const ruleToEdit = {
        ...rule,
        custom_response_body: rule.custom_response_body || '',  // Convert null/undefined to ""
    };
    setEditingRule(ruleToEdit);
    setShowEditModal(true);
};

const handleSave = async () => {
    if (!editingRule) return;

    try {
      // Extract the role_id (assuming it's part of editingRule)
      const { id, ...ruleDataWithoutId } = editingRule; // Destructure to exclude id
       // Now add id to ruleData
       const ruleData = {
        ...ruleDataWithoutId,
        id: id,
       };

      const response = await fetch('/Edit/firewall-rules', { 
        method: 'PUT', 
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleData) // Send the entire rule data in the body
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Show error message using alert()
        alert(errorData.message || "Failed to update rule");
        throw new Error(errorData.message || "Failed to update rule"); // Re-throw to stop further execution
      }

      // If the update was successful
      const updatedRule = await response.json(); // Assuming the API returns the updated rule

      // Update the rules state (optimistically)
      setRules(rules.map(rule => (rule.id === updatedRule.id ? updatedRule : rule)));

      // Show success message using alert() and close modal after the user clicks OK
      alert("Rule updated successfully!");
      setShowEditModal(false); // Close the modal
      setEditingRule(null);  // Clear the editing rule state
    } catch (error) {
      console.error("Error updating rule:", error);
      alert("Failed to update rule. Please try again later.");     
    }
  };


 

  

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Total Rules</h3>
            <Shield className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold mt-2">{totalRules}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Medium Severity  Rules</h3>
            <Activity className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold mt-2">{mediumSeverityRules}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">High Severity Rules</h3>
            <AlertTriangle className="text-red-500" size={24} />
          </div>
          <p className="text-3xl font-bold mt-2">{highSeverityRules}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search rules..."
                className="w-full bg-gray-700 rounded-lg pl-10 pr-4 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              className="bg-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterCriteria.severity}
              onChange={(e) => setFilterCriteria({ ...filterCriteria, severity: e.target.value })}
            >
              <option value="">All Severities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              className="bg-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterCriteria.policy_mode}
              onChange={(e) => setFilterCriteria({ ...filterCriteria, policy_mode: e.target.value })}
            >
              <option value="">All Policies</option>
              <option value="Prevention">Prevention</option>
              <option value="Detection">Detection</option>
              <option value="Learning">Learning</option>
            </select>
            <select
              className="bg-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterCriteria.rule_type}
              onChange={(e) => setFilterCriteria({ ...filterCriteria, rule_type: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="Predefined">Predefined</option>
              <option value="Custom">Custom</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rules Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-10">
            Loading... {/* Or use a spinner component */}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="px-6 py-3 text-left">Rule Name</th>
                    <th className="px-6 py-3 text-left">Category</th>
                    <th className="px-6 py-3 text-left">Type</th>
                    <th className="px-6 py-3 text-left">Severity</th>
                    <th className="px-6 py-3 text-left">Policy Mode</th>
                    <th className="px-6 py-3 text-left">Action</th>
                    <th className="px-6 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                {currentItems.map(rule => (
                    <tr 
                    key={rule.id} 
                    className="border-t border-gray-700 hover:bg-gray-700/50 transition-colors duration-200"
                    >
                    <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-100">{rule.rule_name}</span>
                        </div>
                    </td>
                    
                    <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-md bg-gray-700/50 text-gray-300 text-sm">
                        {rule.active_rule_category}
                        </span>
                    </td>
                    
                    <td className="px-6 py-4">
                        <span className="text-sm text-gray-400 font-medium">
                        {rule.rule_type === 'Predefined' ? (
                            <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            Predefined
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            Custom
                            </span>
                        )}
                        </span>
                    </td>
                    
                    <td className="px-6 py-4">
                        <SeverityIndicator severity={rule.severity} />
                    </td>
                    
                    <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-md text-sm ${
                        rule.policy_mode === 'Prevention' 
                            ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-500/50' 
                            : 'bg-orange-500/20 text-orange-200 border border-orange-500/50'
                        }`}>
                        {rule.policy_mode}
                        </span>
                    </td>
                    
                    <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-md text-sm ${
                        rule.default_action === 'Block' 
                            ? 'bg-red-500/20 text-red-200 border border-red-500/50' 
                            : rule.default_action === 'Allow'
                            ? 'bg-green-500/20 text-green-200 border border-green-500/50'
                            : 'bg-blue-500/20 text-blue-200 border border-blue-500/50'
                        }`}>
                        {rule.default_action}
                        </span>
                    </td>
                    
                    <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleEdit(rule)}
                            className="p-2 rounded-lg hover:bg-gray-600/50 text-blue-400 hover:text-blue-300 transition-all duration-200"
                            title="Edit Rule"
                        >
                            <Edit2 size={16} />
                        </button>
                        <button
                            onClick={() => handleDelete(rule.id)}
                            className="p-2 rounded-lg hover:bg-gray-600/50 text-red-400 hover:text-red-300 transition-all duration-200"
                            title="Delete Rule"
                        >
                            <X size={16} />
                        </button>
                        </div>
                    </td>
                    </tr>
                ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end p-4">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-l ${currentPage === 1 ? 'bg-gray-700 text-gray-400' : 'bg-gray-600 hover:bg-gray-500 text-white'} transition-colors`}
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="mx-2 text-gray-400">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-r ${currentPage === totalPages ? 'bg-gray-700 text-gray-400' : 'bg-gray-600 hover:bg-gray-500 text-white'} transition-colors`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Edit Rule</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Rule Name</label>
                <input
                  type="text"
                  value={editingRule.rule_name}
                  onChange={(e) => setEditingRule({ ...editingRule, rule_name: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Severity</label>
                <select
                  value={editingRule.severity}
                  onChange={(e) => setEditingRule({ ...editingRule, severity: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Policy Mode</label>
                <select
                  value={editingRule.policy_mode}
                  onChange={(e) => setEditingRule({ ...editingRule, policy_mode: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Prevention">Prevention</option>
                  <option value="Detection">Detection</option>
                  <option value="Learning">Learning Mode</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Default Action</label>
                <select
                  value={editingRule.default_action}
                  onChange={(e) => setEditingRule({ ...editingRule, default_action: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Block">Block</option>
                  <option value="Alert">Alert</option>
                  <option value="Challenge">CAPTCHA Challenge</option>
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Rule Pattern</label>
                <input
                  type="text"
                  value={editingRule.custom_rule_pattern}
                  onChange={(e) => setEditingRule({ ...editingRule, custom_rule_pattern: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Custom Response</label>
                <textarea
                  value={editingRule.custom_response_body}
                  onChange={(e) => setEditingRule({ ...editingRule, custom_response_body: e.target.value })}
                  className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                />
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-4">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 transition-colors flex items-center"
              >
                <Save size={18} className="mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirewallDashboard;